export const SOLUTIONS = [
  { slug: "never-miss-call", problem: "Never Miss a Call", product: "Freedom 24/7 AI Receptionist", description: "An AI voice receptionist that answers every call — day or night — with the warmth of your best front desk and the consistency you've always wished for.", span: "lg:col-span-8" },
  { slug: "book-appointments", problem: "Book More Appointments", product: "AI Appointment Booking", description: "Self-serve scheduling that fills your calendar while you sleep.", span: "lg:col-span-4" },
  { slug: "capture-leads", problem: "Capture Every Lead", product: "Lead Qualification AI", description: "Smart triage that gathers details, scores intent and hands off only the leads worth your time.", span: "lg:col-span-4" },
  { slug: "follow-up", problem: "Follow Up Automatically", product: "SMS + Email Automation", description: "Hands-off nurturing across SMS and email — written in your brand voice, sent at the right moment.", span: "lg:col-span-4" },
  { slug: "customer-support", problem: "Customer Support", product: "AI Voice Agent", description: "Resolve common inquiries in seconds, escalate the rest gracefully.", span: "lg:col-span-4" },
  { slug: "team-assistant", problem: "Internal Team Assistant", product: "Employee AI Assistant", description: "A private AI workspace for your team — onboarding, SOPs, and answers, instantly.", span: "lg:col-span-8" },
  { slug: "crm-automation", problem: "CRM Automation", product: "Sales Pipeline Automation", description: "Auto-update records, auto-log calls, auto-advance deals.", span: "lg:col-span-4" },
];

// Industries section cards. Each slug matches a Lead Gen caseStudies id for the deep link.
export const INDUSTRIES = [
  { slug: "spas-beauty", name: "Spas & Beauty", tagline: "Fill the treatment calendar, week after week.", img: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&q=80" },
  { slug: "hair-salons", name: "Hair Salons", tagline: "More new clients in every stylist's chair.", img: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=800&q=80" },
  { slug: "nail-salons", name: "Nail Salons", tagline: "Manicure and pedicure bookings, midweek too.", img: "https://images.unsplash.com/photo-1632345031435-8727f6897d53?w=800&q=80" },
  { slug: "real-estate", name: "Real Estate", tagline: "Show more homes. Miss fewer leads.", img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&q=80" },
  { slug: "hvac", name: "HVAC", tagline: "Dispatch faster than your competition.", img: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&q=80" },
  { slug: "plumbing", name: "Plumbing", tagline: "Never miss an emergency call.", img: "https://static.prod-images.emergentagent.com/jobs/7e5dce10-e16f-4e45-8888-2db12d87b01c/images/c41b7a90768c69f185dc47992ad4b2adb72599fe56630b03f6d578a4d07b3f18.jpeg" },
];

// Earlier industry pages kept reachable by direct URL only (not shown in the Industries section).
const LEGACY_INDUSTRY_PAGES = [
  { slug: "insurance", name: "Insurance", tagline: "Quote, qualify, and close — automated.", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80" },
  { slug: "law", name: "Law Firms", tagline: "Intake without the bottleneck.", img: "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&q=80" },
  { slug: "contractors", name: "Contractors", tagline: "Bid more jobs. Book more crews.", img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80" },
  { slug: "medical", name: "Medical", tagline: "Compassionate intake, every hour.", img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80" },
  { slug: "dental", name: "Dental", tagline: "Recall, reschedule, recover revenue.", img: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=800&q=80" },
  { slug: "med-spa", name: "Med Spa", tagline: "A concierge experience, automated.", img: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&q=80" },
  { slug: "property-mgmt", name: "Property Management", tagline: "After-hours requests, handled.", img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80" },
  { slug: "restaurants", name: "Restaurants", tagline: "Reservations & takeout, all in one voice.", img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80" },
];

export const INDUSTRY_PAGES = [...INDUSTRIES, ...LEGACY_INDUSTRY_PAGES];

// Monthly service footnote applied to every PAID recurring service (not Strategy Session).
export const MONTHLY_SERVICE_FOOTNOTE =
  "Monthly service covers routine maintenance, technical support, troubleshooting, and minor adjustments to existing configurations. New features, additional integrations, and substantial modifications are quoted separately.";

const CASE_STUDY_DISCLAIMER =
  "This scenario is illustrative and provided for context only. It does not reflect a specific client engagement, and Freedom By AI Design does not guarantee any particular lead volume, follow-up success, or business result. Each campaign is scoped to the client's industry, service area, and agreed qualification criteria.";


export const PACKAGES = [
  {
    id: "consulting_session",
    name: "Strategy Session",
    subtitle: "Start here.",
    price: 0,
    period: "free",
    features: [
      "60-min AI strategy call",
      "Custom opportunity map",
      "Personalized action plan",
      "Recording + summary",
    ],
    featured: false,
    isFree: true,
  },
  {
    id: "receptionist_setup",
    name: "AI Receptionist",
    subtitle: "Setup starts at",
    price: 1450,
    period: "setup",
    monthly: 225,
    monthlyLabel: "mo service",
    features: [
      "24/7 call handling and customer inquiries",
      "Lead qualification + appointment scheduling",
      "Customer information capture",
      "CRM integration & updates where configured",
      "Routine after-hours communication",
    ],
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: false,
  },
  {
    id: "automation_starter",
    name: "AI Automation Suite",
    subtitle: "Setup starts at",
    price: 2100,
    period: "setup",
    monthly: 397,
    monthlyLabel: "mo service",
    features: [
      "Customized AI-powered business processes",
      "CRM + customer communication automation",
      "Lead capture, follow-up & scheduling workflows",
      "Repetitive administrative task automation",
      "Connected operational systems",
      "Workflow monitoring & optimization",
    ],
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: true,
  },
  {
    id: "business_automations",
    name: "Business Automations",
    subtitle: "Setup starts at",
    price: 1250,
    period: "setup",
    monthly: 125,
    monthlyLabel: "mo service",
    features: [
      "Automated reminders & notifications",
      "CRM data capture and updates",
      "Customer follow-up automation",
      "Task & administrative process automation",
      "Automated internal notifications",
      "Reduced manual data entry",
    ],
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: false,
  },
  {
    id: "workflow_integration",
    name: "Workflow Integration",
    subtitle: "Setup starts at",
    price: 1250,
    period: "setup",
    monthly: 125,
    monthlyLabel: "mo service",
    features: [
      "CRM integration",
      "Scheduling & calendar connections",
      "Lead management workflows",
      "Customer communication integration",
      "Internal task routing",
      "Connected business processes",
    ],
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: false,
  },
  {
    id: "lead_generation",
    name: "AI-Powered Lead Generation",
    subtitle: "Setup starts at",
    price: 450,
    period: "setup",
    priceSuffix: "includes 4 qualified leads",
    monthly: 125,
    monthlyLabel: "mo campaign management",
    valueSummary: "4 Qualified Leads Included • AI-Powered Prospecting • Ongoing Campaign Management",
    features: [
      "Targeted lead generation campaigns",
      "AI-assisted prospect research & qualification",
      "4 initial qualified leads delivered to client",
      "Ongoing campaign monitoring & optimization",
      "Performance reporting",
      "Additional qualified leads: $75 each",
      "Additional AI agents (if created): $75 each",
    ],
    whatYouReceive: [
      "Four qualified leads included with initial setup",
      "Additional qualified leads available at $75 each",
      "AI-powered prospect identification and qualification",
      "Ongoing campaign management and optimization",
      "Required $30/day advertising budget managed by Freedom By AI Design",
      "Qualified leads delivered directly to the client's business for follow-up",
    ],
    whoWeHelp: {
      industries: [
        "Spas and beauty businesses",
        "Hair salons",
        "Manicure and pedicure salons",
        "Real estate professionals",
        "HVAC companies",
        "Plumbing companies",
      ],
      closingStatement: "And other businesses looking to attract qualified customers and grow.",
    },
    caseStudies: [
    {
      id: "spas-beauty",
      industry: "Spa & Beauty",
      label: "Illustrative Example",
      business: "Lumen Spa & Beauty — Denver, CO",
      challenge:
        "A seven-chair day spa with weekend demand far outpacing front-desk capacity. Contact-form submissions arriving after business hours were going unanswered for 48+ hours, and the owner felt inquiries were cooling before anyone could reply.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the spa's service area, matched to the owner's agreed qualification criteria (service radius, treatment interest, preferred contact window). Qualified prospects were delivered straight to the spa's booking inbox for the front-desk team to follow up on.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "11 additional qualified leads delivered through ongoing campaign management",
        "15 total qualified leads routed to the spa's inbox during the first month",
      ],
      outcome:
        "The owner reported a shorter queue of unreplied inquiries at the end of each week and a steadier mid-week calendar. The spa opted to continue month-over-month campaign management to keep qualified leads flowing.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    {
      id: "hair-salons",
      industry: "Hair Salons",
      label: "Illustrative Example",
      business: "Copper & Comb Hair Studio — Boulder, CO",
      challenge:
        "A six-chair hair studio with a loyal base of regulars but few new clients coming through the door. Two newer stylists had open afternoons most weeks, and the owner wanted a steadier flow of first-time color and cut appointments.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the studio's local area, matched to the owner's agreed qualification criteria (service interest, location, preferred appointment days). Qualified prospects were delivered to the studio's booking inbox for the front desk to schedule with available stylists.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "9 additional qualified leads delivered through ongoing campaign management",
        "13 total qualified leads routed to the studio's inbox during the first month",
      ],
      outcome:
        "The owner reported fewer empty afternoon slots on the newer stylists' books and a clearer view of which services new clients were asking about. The studio opted to continue month-over-month campaign management.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    {
      id: "nail-salons",
      industry: "Nail Salons",
      label: "Illustrative Example",
      business: "Polished Nail Lounge — Littleton, CO",
      challenge:
        "A ten-station manicure and pedicure salon that was packed on weekends but quiet Tuesday through Thursday. Most new clients were walk-ins, and the owner wanted more pre-booked appointments to keep technicians busy midweek.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the salon's surrounding neighborhoods, matched to the owner's agreed qualification criteria (service type, location, preferred visit days). Qualified prospects were delivered to the salon's inbox for the team to confirm bookings.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "10 additional qualified leads delivered through ongoing campaign management",
        "14 total qualified leads routed to the salon's inbox during the first month",
      ],
      outcome:
        "The owner reported more pre-booked midweek appointments and less reliance on walk-in traffic. The salon opted to continue month-over-month campaign management.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    {
      id: "real-estate",
      industry: "Real Estate",
      label: "Illustrative Example",
      business: "Keystone Realty Partners — Highlands Ranch, CO",
      challenge:
        "A three-agent residential team leaning on referrals and open houses for new business. Pipeline gaps between closings made monthly income unpredictable, and the team lead wanted a steadier source of motivated buyer and seller conversations.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the team's farm area, matched to the agreed qualification criteria (buy or sell intent, price range, timeline). Qualified prospects were delivered to the team's shared inbox for agents to follow up on.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "6 additional qualified leads delivered through ongoing campaign management",
        "10 total qualified leads routed to the team's inbox during the first month",
      ],
      outcome:
        "The team lead reported a fuller calendar of consultation calls and fewer quiet weeks between referrals. The team opted to continue month-over-month campaign management.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    {
      id: "hvac",
      industry: "HVAC",
      label: "Illustrative Example",
      business: "Summit Air Heating & Cooling — Aurora, CO",
      challenge:
        "A four-truck HVAC contractor whose demand swung sharply with the seasons. Early-spring and late-fall shoulder months left technicians under-booked, and the owner had no steady source of qualified maintenance and replacement inquiries between heat waves and cold snaps.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the company's service radius, matched to the owner's agreed qualification criteria (homeowner status, system type, service versus replacement interest). Qualified prospects were delivered to the dispatch inbox for the office team to schedule.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "8 additional qualified leads delivered through ongoing campaign management",
        "12 total qualified leads routed to the dispatch inbox during the first month",
      ],
      outcome:
        "The owner reported fewer open slots on the shoulder-season schedule and a clearer picture of which neighborhoods were requesting tune-ups. The company opted to continue month-over-month campaign management heading into peak season.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    {
      id: "plumbing",
      industry: "Plumbing",
      label: "Illustrative Example",
      business: "Front Range Plumbing Co. — Lakewood, CO",
      challenge:
        "A family-owned plumbing company relying almost entirely on word of mouth. Emergency calls kept the crew busy, but the owner wanted more planned work — water heater replacements, repiping, and fixture upgrades — to smooth out an unpredictable weekly schedule.",
      solution:
        "Freedom By AI Design launched a targeted lead generation campaign across the company's service area, matched to the owner's agreed qualification criteria (project type, property ownership, preferred timeline). Qualified prospects were delivered to the office inbox for the team to quote and book.",
      deliverySummary: "First 30 days of campaign activity",
      delivery: [
        "4 qualified leads delivered with the initial setup",
        "7 additional qualified leads delivered through ongoing campaign management",
        "11 total qualified leads routed to the office inbox during the first month",
      ],
      outcome:
        "The owner reported a more predictable mix of planned projects alongside emergency calls and better visibility into upcoming quote requests. The company opted to continue month-over-month campaign management.",
      disclaimer: CASE_STUDY_DISCLAIMER,
    },
    ],
    ctaLabel: "Let's Find Your Next Customers",
    note:
      "Mandatory ad spend: $30/day advertising budget is required and paid separately to Freedom By AI Design, in addition to setup, campaign management, and per-lead fees. Qualified lead criteria agreed in advance; FBAD does not guarantee a lead will convert to a paying customer.",
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: false,
  },
  {
    id: "website_chatbot",
    name: "Professional Website + AI Chatbot",
    subtitle: "Setup starts at",
    price: 1700,
    period: "setup",
    monthly: 75,
    monthlyLabel: "mo chatbot maintenance only",
    features: [
      "4 professionally designed, mobile-responsive pages",
      "Brand-aligned design with clear calls to action",
      "Contact form & basic lead capture",
      "Basic on-page SEO + social links",
      "AI chatbot installation & initial configuration",
      "2 rounds of pre-launch revisions",
      "Domain connection & SSL where supported",
    ],
    note:
      "The $75/month covers chatbot maintenance only — NOT full website maintenance. Does not include e-commerce, custom software, ongoing SEO, paid ads, additional pages, or major redesigns (quoted separately).",
    footnote: MONTHLY_SERVICE_FOOTNOTE,
    featured: false,
  },
];

export const FAQS = [
  { q: "Will AI replace my receptionist?", a: "No — it elevates them. The AI handles overflow, after-hours, and qualification so your team can focus on high-value conversations." },
  { q: "Can it transfer calls?", a: "Yes. We configure live transfer rules to anyone on your team, with intelligent routing by intent." },
  { q: "Can it integrate with my CRM?", a: "We integrate with HubSpot, GoHighLevel, Salesforce, Pipedrive, and most major CRMs via API or Zapier." },
  { q: "How quickly can it be installed?", a: "Most receptionists go live in 7–14 days. Complex automation projects take 3–6 weeks." },
  { q: "What industries do you serve?", a: "Spas and beauty businesses, hair salons, nail salons (manicure and pedicure services), real estate professionals, HVAC companies, and plumbing companies — plus other service businesses looking to attract qualified customers and grow." },
  { q: "Is my data secure?", a: "Yes. Security is a top priority at Freedom By AI Design. Our AI Receptionists are built on GoHighLevel's secure infrastructure using enterprise-grade encryption, SOC 2 Type II-certified infrastructure, multi-factor authentication, role-based access controls, and continuous security monitoring to help protect your business and customer data.\n\nYour AI Receptionist only has access to the information, systems, and integrations you intentionally authorize. It does not automatically gain access to your entire business. We also follow security best practices, including strong password policies, two-factor authentication on administrator accounts, regular reviews of user access and connected integrations, and limiting unnecessary sensitive information within the AI's knowledge base.\n\nFor businesses with additional regulatory or compliance requirements, enhanced security and compliance options may be available. While Freedom By AI Design provides a secure platform and follows industry best practices, clients are responsible for maintaining appropriate account security, user permissions, and internal data-handling practices." },
];

export const TESTIMONIALS = [
  { name: "Sophia Renard", role: "Founder, Renard Wellness Collective", quote: "Freedom transformed how we handle calls. Warm, on-brand, 24/7 — we've never missed a lead since." },
  { name: "Marcus Ellison", role: "CEO, Ellison Real Estate Group", quote: "It felt like hiring a full-time operations manager. Smarter, faster, and always on." },
  { name: "Camille Osei", role: "Director, Osei Creative Agency", quote: "They understood our brand and built something that feels genuinely human." },
];

export const CASE_STUDIES = [
  { client: "Denver Realtor", problem: "Missing calls during showings.", solution: "Freedom AI Receptionist", results: ["38% more answered calls", "19 appointments booked", "Zero missed weekend inquiries"] },
  { client: "Med Spa, Cherry Creek", problem: "After-hours booking lost.", solution: "AI Receptionist + Booking", results: ["52 after-hours bookings/mo", "27% lift in repeat visits", "Recovered $14k/mo"] },
  { client: "HVAC Contractor", problem: "Lead leakage during dispatch.", solution: "Lead Qualification AI", results: ["3x qualified leads/day", "21% close rate increase", "Same headcount"] },
];

export const DEMO_VIDEOS = [
  { title: "AI Receptionist – Live Demo", duration: "0:48", thumb: "https://images.unsplash.com/photo-1589254065878-42c9da997008?w=800&q=80" },
  { title: "Appointment Booking Flow", duration: "1:12", thumb: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80" },
  { title: "Lead Qualification in Action", duration: "1:05", thumb: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&q=80" },
  { title: "Call Summary + CRM Sync", duration: "0:55", thumb: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80" },
  { title: "AI Voice Agent", duration: "1:20", thumb: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80" },
  { title: "Customer Follow-up Sequence", duration: "0:42", thumb: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80" },
];
